/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Form,
  Row,
  Col,
  Spin,
  Modal,
  Input,
  Button,
} from 'antd';
import { onError } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import Select from 'components/Select';
import ModalDelete from 'components/ModalDelete';
import {
  baseURI,
  getConfiguracionesDeAsignacionDePlacas,
  postConfiguracionDeAsignacionDePlacas,
  patchConfiguracionDeAsignacionDePlacas,
  deleteConfiguracionDeAsignacionDePlacas,
} from 'api/recaudacion/configuracionDeAsignacionDePlacas';
import usePaginatedFetcher from 'hooks/usePaginatedFetcher';
import { getClasesDeVehiculos, getServiciosDeVehiculo } from 'api/recaudacion';

const ConfiguracionDeAsignacionesDePlacas = ({ permission }) => {
  // eslint-disable-next-line no-param-reassign
  permission.import = true;
  const [form] = Form.useForm();
  const [
    data,
    fetchData,
    loadingFetcher,
  ] = usePaginatedFetcher(getConfiguracionesDeAsignacionDePlacas,
    { auto: true, onlyOnePage: true });
  const estadosGlobales = useSelector(({ catalogs }) => catalogs.estadosGlobales);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [_loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [clases, setClases] = useState([]);
  const [servicios, setServicios] = useState([]);

  const loading = loadingFetcher || _loading;

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      const [_servicios, _clases] = await Promise.all([
        getServiciosDeVehiculo(),
        getClasesDeVehiculos(),
      ]);
      if (mounted) {
        setServicios(_servicios);
        setClases(_clases);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    setSelectedRowKeys([]);
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickEdit = async () => {
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const [key] = selectedRowKeys;
      const _data = await (key ? patchConfiguracionDeAsignacionDePlacas(key, values)
        : postConfiguracionDeAsignacionDePlacas(values));
      if (_data) {
        await fetchData();
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const deleteItem = async () => {
    setLoading(true);
    if (selectedRowKeys.length) {
      const [key] = selectedRowKeys;
      const deleted = await deleteConfiguracionDeAsignacionDePlacas(key);
      if (deleted) {
        setVisibleAlert(false);
        onCancel();
        await fetchData();
      }
    }
    setLoading(false);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const rules = { required: [{ required: true }] };

  const columns = [
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 300,
    },
    {
      titleText: 'Servicio',
      dataIndex: 'servicio',
      key: 'servicio',
      render: (val) => (servicios.find((item) => item.id === val).nombre),
      catalog: {
        cat: servicios,
        labelProp: 'nombre',
      },
    },
    {
      titleText: 'Clase',
      dataIndex: 'clase_del_vehiculo',
      key: 'clase_del_vehiculo',
      render: (val) => (clases.find((item) => item.id === val).descripcion),
      catalog: {
        cat: clases,
        labelProp: 'descripcion',
      },
    },
    {
      titleText: 'Estado',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      render: (val) => (estadosGlobales.find((item) => item.id === val).descripcion),
      catalog: {
        cat: estadosGlobales,
        labelProp: 'descripcion',
      },
    },
  ];

  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data.results}
          rowKey="id"
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit,
            onClickDelete,
          }}
          loading={loading}
          permission={permission}
          pagination={false}
          paginationCallback={(page, params) => fetchData(page, null, params)}
          count={data.count}
          currentPage={data.page}
          baseURI={baseURI}
        />
        <Modal
          visible={visible}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? ' Editar' : 'Agregar'} Configuración de asignaciones de placas`}
              onCancel={onCancel}
              loading={loading}
              onFinish={onFinish}
              permission={permission}
            />
          )}
          keyboard={!loading}
          maskClosable={!loading}
          footer={null}
          closable={false}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            initialValues={{ estados_globales: 1 }}
            onValuesChange={(changed, values) => {
              if ('servicio' in changed || 'clase_del_vehiculo' in changed) {
                const { servicio, clase_del_vehiculo } = values;
                const _servicio = servicios.find((e) => e.id === servicio)?.nombre || '';
                const _clase = clases.find((e) => e.id === clase_del_vehiculo)?.descripcion || '';
                form.setFieldsValue({ descripcion: `${_servicio}${_clase && ' - '}${_clase}` });
              }
            }}
          >
            <Row>
              <Col span={24}>
                <Form.Item
                  name="descripcion"
                  label="Descripción"
                  rules={rules.required}
                  tooltip="Se recomienda mantener las descripciones de Servicio y Clase para mantener los registros identificables"
                >
                  <Input allowClear />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="servicio"
                  label="Servicio"
                  rules={rules.required}
                >
                  <Select dataSource={servicios} labelProp="nombre" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="clase_del_vehiculo"
                  label="Clase"
                >
                  <Select dataSource={clases} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="estados_globales"
                  rules={rules.required}
                  label="Estado"
                  hasFeedback
                >
                  <Select dataSource={estadosGlobales} disabled={!selectedRowKeys.length} />
                </Form.Item>
              </Col>
              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Row>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          visible={visibleAlert}
          content={`Configuración de asignaciones de placas ${form.getFieldValue('descripcion')}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default ConfiguracionDeAsignacionesDePlacas;
